import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  Package, CheckCircle2, XCircle, Clock, DollarSign, Users, ClipboardList,
  TrendingUp, Plane, ArrowRight, Wallet,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from "recharts";

const KpiCard = ({ label, value, icon: Icon, accent, onClick, testId, sub }) => (
  <Card
    data-testid={testId}
    onClick={onClick}
    className={`p-5 group transition-all duration-200 min-w-0 ${onClick ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5 hover:border-teal-200" : ""}`}
  >
    <div className="flex items-center justify-between gap-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold whitespace-nowrap truncate">{label}</div>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${accent}`}><Icon className="w-4 h-4" /></div>
    </div>
    <div className="font-heading text-2xl xl:text-3xl font-bold text-[var(--brand-accent)] mt-2 tabular-nums truncate" title={String(value)}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    {onClick && (
      <div className="mt-2 text-[10px] text-[var(--brand-primary)] opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1">
        View details <ArrowRight className="w-2.5 h-2.5" />
      </div>
    )}
  </Card>
);

// Pretty currency formatter: $1.97k, $1.5M, etc.
const fmtMoney = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}k`;
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const PIE_COLORS = ["#0d9488", "#14b8a6", "#86efac", "#fbbf24", "#a78bfa", "#f87171"];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); } catch (_e) {}
  })(); }, []);

  if (!stats) return <div className="text-slate-500 p-8">Loading dashboard…</div>;

  const airlineData = (stats.airlines || []).map((a) => ({ ...a, name: `${a.code}`, fullName: a.name }));
  const productData = (stats.product_breakdown || []).map((p) => ({ name: p.product.charAt(0).toUpperCase() + p.product.slice(1), value: p.revenue }));
  const monthlyData = (stats.monthly || []).map((m) => ({ name: m.month.slice(5) + "/" + m.month.slice(2, 4), revenue: m.revenue }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">Overview</h1>
        <p className="text-slate-500 mt-1">Click any card or chart bar to drill down.</p>
      </div>

      {/* Row 1 — primary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Total Sales Revenue" value={fmtMoney(stats.revenue)}
                 sub={`${stats.total_bookings} bookings`}
                 icon={DollarSign} accent="bg-emerald-50 text-emerald-600"
                 testId="kpi-revenue" onClick={() => navigate("/admin/transactions")} />
        <KpiCard label="Profit (markups)" value={fmtMoney(stats.profit || 0)}
                 sub="Sum of all applied markups"
                 icon={TrendingUp} accent="bg-teal-50 text-[var(--brand-primary-dark)]"
                 testId="kpi-profit" onClick={() => navigate("/admin/reports/custom")} />
        <KpiCard label="Confirmed Bookings" value={stats.confirmed}
                 sub={`${Math.round((stats.confirmed / Math.max(stats.total_bookings, 1)) * 100)}% of total`}
                 icon={CheckCircle2} accent="bg-emerald-50 text-emerald-600"
                 testId="kpi-confirmed" onClick={() => navigate("/admin/bookings?status=confirmed")} />
        <KpiCard label="Airlines Sold" value={airlineData.length}
                 sub="Unique marketing carriers"
                 icon={Plane} accent="bg-violet-50 text-violet-600"
                 testId="kpi-airlines" onClick={() => navigate("/admin/suppliers")} />
      </div>

      {/* Row 2 — secondary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Total Bookings" value={stats.total_bookings} icon={Package} accent="bg-teal-50 text-[var(--brand-primary)]"
                 testId="kpi-bookings" onClick={() => navigate("/admin/bookings")} />
        <KpiCard label="Pending Payment" value={stats.pending} icon={Clock} accent="bg-amber-50 text-amber-600"
                 testId="kpi-pending" onClick={() => navigate("/admin/bookings?status=pending_payment")} />
        <KpiCard label="Cancel Requests" value={stats.pending_cancels || 0} icon={ClipboardList} accent="bg-blue-50 text-blue-600"
                 testId="kpi-cancel-reqs" onClick={() => navigate("/admin/cancel-requests")} />
        <KpiCard label="Users" value={stats.users} icon={Users} accent="bg-slate-100 text-slate-700"
                 testId="kpi-users" onClick={() => navigate("/admin/users")} />
      </div>

      {/* Row 3 — Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Airlines bar */}
        <Card className="p-5 lg:col-span-2" data-testid="chart-airlines">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-heading text-lg font-semibold text-[var(--brand-accent)]">Top airlines sold</div>
              <div className="text-xs text-slate-500">Revenue (USD) by marketing carrier. Click a bar to filter bookings.</div>
            </div>
            <div className="text-xs text-slate-400">{airlineData.length} airline{airlineData.length !== 1 ? "s" : ""}</div>
          </div>
          {airlineData.length === 0 ? (
            <div className="text-sm text-slate-400 italic text-center py-12">No paid flight bookings yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={airlineData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => fmtMoney(v)} />
                <Tooltip
                  cursor={{ fill: "#0d948814" }}
                  contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                  formatter={(v) => [`$${Number(v).toFixed(2)}`, "Revenue"]}
                  labelFormatter={(label) => {
                    const a = airlineData.find((x) => x.name === label); return a ? `${a.code} · ${a.fullName}` : label;
                  }}
                />
                <Bar dataKey="revenue" fill="#0d9488" radius={[8, 8, 0, 0]}
                     style={{ cursor: "pointer" }}
                     onClick={(_data, idx) => {
                       const code = airlineData[idx]?.code;
                       if (code) navigate(`/admin/bookings?airline=${code}`);
                     }}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Product split pie */}
        <Card className="p-5" data-testid="chart-products">
          <div>
            <div className="font-heading text-lg font-semibold text-[var(--brand-accent)]">Revenue by product</div>
            <div className="text-xs text-slate-500">Share of paid bookings</div>
          </div>
          {productData.length === 0 ? (
            <div className="text-sm text-slate-400 italic text-center py-12">No data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={productData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={2}
                     style={{ cursor: "pointer" }}
                     onClick={(_d, idx) => {
                       const p = (productData[idx]?.name || "").toLowerCase();
                       if (p) navigate(`/admin/bookings?type=${p}`);
                     }}>
                  {productData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                         formatter={(v, n) => [`$${Number(v).toFixed(2)}`, n]} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Row 4 — Monthly trend + airline table */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-1" data-testid="chart-monthly">
          <div>
            <div className="font-heading text-lg font-semibold text-[var(--brand-accent)]">Last 6 months</div>
            <div className="text-xs text-slate-500">Paid revenue trend</div>
          </div>
          {monthlyData.length === 0 ? (
            <div className="text-sm text-slate-400 italic text-center py-12">No data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={monthlyData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => fmtMoney(v)} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                         formatter={(v) => [`$${Number(v).toFixed(2)}`, "Revenue"]} />
                <Line type="monotone" dataKey="revenue" stroke="#0d9488" strokeWidth={3}
                      dot={{ r: 4, fill: "#0d9488" }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="lg:col-span-2 overflow-auto" data-testid="airlines-table">
          <div className="p-5 pb-3">
            <div className="font-heading text-lg font-semibold text-[var(--brand-accent)]">Airlines breakdown</div>
            <div className="text-xs text-slate-500">Click a row to filter bookings by that carrier.</div>
          </div>
          {airlineData.length === 0 ? (
            <div className="px-5 pb-8 text-sm text-slate-400 italic text-center">No paid flight bookings yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-2 font-bold">Code</th>
                  <th className="px-5 py-2 font-bold">Airline</th>
                  <th className="px-5 py-2 font-bold text-right">Bookings</th>
                  <th className="px-5 py-2 font-bold text-right">Revenue</th>
                  <th className="px-5 py-2 font-bold text-right">Profit</th>
                </tr>
              </thead>
              <tbody>
                {airlineData.map((a) => (
                  <tr key={a.code} data-testid={`airline-row-${a.code}`}
                      onClick={() => navigate(`/admin/bookings?airline=${a.code}`)}
                      className="border-t border-slate-100 cursor-pointer hover:bg-teal-50/40 transition-colors">
                    <td className="px-5 py-2 font-mono font-bold text-[var(--brand-primary-dark)]">{a.code}</td>
                    <td className="px-5 py-2 text-slate-700">{a.fullName}</td>
                    <td className="px-5 py-2 text-right">{a.bookings}</td>
                    <td className="px-5 py-2 text-right font-mono">${a.revenue.toFixed(2)}</td>
                    <td className="px-5 py-2 text-right font-mono text-emerald-700">${a.profit.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}
